// react_frontend/src/Components/Modules/Accounting/ChartOfAccounts.js
import React, { useState, useEffect } from 'react';
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import {
    Box, Typography, Paper, TextField, Button, IconButton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Chip, IconButton as MuiIconButton, InputAdornment, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaFolder, FaFile } from 'react-icons/fa';
import Swal from 'sweetalert2';

const accountTypes = [
    { value: 'asset', label: 'Asset' },
    { value: 'liability', label: 'Liability' },
    { value: 'equity', label: 'Equity' },
    { value: 'revenue', label: 'Revenue' },
    { value: 'expense', label: 'Expense' },
];

const accountCategories = {
    asset: [
        { value: 'current_asset', label: 'Current Asset' },
        { value: 'fixed_asset', label: 'Fixed Asset' },
        { value: 'intangible_asset', label: 'Intangible Asset' },
    ],
    liability: [
        { value: 'current_liability', label: 'Current Liability' },
        { value: 'long_term_liability', label: 'Long Term Liability' },
    ],
    equity: [{ value: 'equity', label: 'Equity' }],
    revenue: [
        { value: 'operating_revenue', label: 'Operating Revenue' },
        { value: 'other_revenue', label: 'Other Revenue' },
    ],
    expense: [
        { value: 'operating_expense', label: 'Operating Expense' },
        { value: 'other_expense', label: 'Other Expense' },
    ],
};

const typeColors = {
    asset: '#1976d2',
    liability: '#d32f2f',
    equity: '#7b1fa2',
    revenue: '#2e7d32',
    expense: '#ef6c00',
};

export default function ChartOfAccounts() {
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({
        account_code: '', account_name: '', account_type: 'asset',
        account_category: '', parent_id: '', opening_balance: 0, balance_type: 'debit', description: ''
    });

    const fetchAccounts = async () => {
        setLoading(true);
        try {
            const res = await AxiosAuthInstance.get('accounting/accounts', { params: { search } });
            setAccounts(res.data.accounts || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAccounts(); }, [search]);

    const openDialog = (account = null) => {
        if (account) {
            setEditing(account);
            setForm({
                account_code: account.account_code,
                account_name: account.account_name,
                account_type: account.account_type,
                account_category: account.account_category || '',
                parent_id: account.parent_id || '',
                opening_balance: account.opening_balance || 0,
                balance_type: account.balance_type || 'debit',
                description: account.description || '',
            });
        } else {
            setEditing(null);
            setForm({
                account_code: '', account_name: '', account_type: 'asset',
                account_category: '', parent_id: '', opening_balance: 0, balance_type: 'debit', description: ''
            });
        }
        setDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            const url = editing ? `accounting/accounts/${editing.id}` : 'accounting/accounts';
            const method = editing ? 'put' : 'post';
            const res = await AxiosAuthInstance[method](url, form);
            if (res.data.status) {
                Swal.fire({ icon: 'success', title: 'Success!', timer: 1500, showConfirmButton: false });
                setDialogOpen(false);
                fetchAccounts();
            }
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed' });
        }
    };

    const handleDelete = async (account) => {
        const result = await Swal.fire({
            title: 'Delete Account?', text: 'This cannot be undone.', icon: 'warning',
            showCancelButton: true, confirmButtonColor: '#f44336',
        });
        if (result.isConfirmed) {
            try {
                await AxiosAuthInstance.delete(`accounting/accounts/${account.id}`);
                Swal.fire({ icon: 'success', title: 'Deleted!', timer: 1500, showConfirmButton: false });
                fetchAccounts();
            } catch (err) {
                Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed' });
            }
        }
    };

    const handleSeed = async () => {
        const result = await Swal.fire({
            title: 'Seed Default Accounts?', text: 'This will add standard chart of accounts.',
            icon: 'question', showCancelButton: true,
        });
        if (result.isConfirmed) {
            try {
                const res = await AxiosAuthInstance.post('accounting/accounts/seed');
                Swal.fire({ icon: 'success', title: 'Seeded!', text: `${res.data.count} accounts created`, timer: 2000 });
                fetchAccounts();
            } catch (err) {
                Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message });
            }
        }
    };

    return (
        <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
            <Breadcrumb title="Chart of Accounts" />

            <Paper sx={{ p: 3, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" fontWeight={700}>Chart of Accounts</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField size="small" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><FaSearch /></InputAdornment> }} />
                    <Button variant="contained" startIcon={<FaPlus />} onClick={() => openDialog()}>Add Account</Button>
                    <Button variant="outlined" onClick={handleSeed}>Seed Defaults</Button>
                </Box>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                            <TableCell>Code</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Balance Type</TableCell>
                            <TableCell>Opening Balance</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {accounts.map((acc) => (
                            <TableRow key={acc.id} hover>
                                <TableCell><code>{acc.account_code}</code></TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {acc.parent_id ? <FaFile /> : <FaFolder />}
                                        <strong>{acc.account_name}</strong>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Chip label={acc.account_type} size="small"
                                        sx={{ bgcolor: `${typeColors[acc.account_type]}20`, color: typeColors[acc.account_type], fontWeight: 600 }} />
                                </TableCell>
                                <TableCell>{acc.account_category || '-'}</TableCell>
                                <TableCell><Chip label={acc.balance_type} size="small" /></TableCell>
                                <TableCell>৳{Number(acc.opening_balance).toLocaleString()}</TableCell>
                                <TableCell align="center">
                                    <IconButton size="small" onClick={() => openDialog(acc)} sx={{ color: '#1976d2' }}><FaEdit /></IconButton>
                                    {!acc.is_system && <IconButton size="small" onClick={() => handleDelete(acc)} sx={{ color: '#f44336' }}><FaTrash /></IconButton>}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editing ? 'Edit Account' : 'Add Account'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Account Code" value={form.account_code} onChange={(e) => setForm({ ...form, account_code: e.target.value })} fullWidth />
                        <TextField label="Account Name" value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} fullWidth />
                        <FormControl fullWidth>
                            <InputLabel>Account Type</InputLabel>
                            <Select value={form.account_type} label="Account Type"
                                onChange={(e) => setForm({ ...form, account_type: e.target.value, account_category: '' })}>
                                {accountTypes.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select value={form.account_category} label="Category"
                                onChange={(e) => setForm({ ...form, account_category: e.target.value })}>
                                {(accountCategories[form.account_type] || []).map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>Balance Type</InputLabel>
                            <Select value={form.balance_type} label="Balance Type"
                                onChange={(e) => setForm({ ...form, balance_type: e.target.value })}>
                                <MenuItem value="debit">Debit</MenuItem>
                                <MenuItem value="credit">Credit</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField label="Opening Balance" type="number" value={form.opening_balance}
                            onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} fullWidth />
                        <TextField label="Description" value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })} fullWidth multiline rows={2} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}